import {
  synapticJwtClaimsSchema,
  synapticRoleSchema,
  type SynapticJwtClaims,
  type SynapticRole,
} from "@/schemas/auth";

export type { SynapticJwtClaims, SynapticRole } from "@/schemas/auth";

export const JWT_STORAGE_KEY = "jwt";

export const SYNAPTIC_IDENTITY_ORIGIN = requiredClientUrl(
  "VITE_SYNAPTIC_IDENTITY_ORIGIN",
  import.meta.env.VITE_SYNAPTIC_IDENTITY_ORIGIN,
);
export const SYNAPTIC_PROFILE_URL = requiredClientUrl(
  "VITE_SYNAPTIC_PROFILE_URL",
  import.meta.env.VITE_SYNAPTIC_PROFILE_URL,
);

export const SYNAPTIC_ROLES = synapticRoleSchema.options;

export type SynapticUser = {
  id: string;
  email: string;
  name?: string;
  picture?: string;
  roles: SynapticRole[];
};

export type SynapticCapability = "canViewEmail" | "canModerate" | "canEdit" | "canDevelop" | "canAdminister";

export type SynapticAuthCallbackStatus =
  | "stored"
  | "invalid-token"
  | "missing-token"
  | "registration";

type SynapticTokenStorage = Pick<Storage, "removeItem" | "setItem">;

const CAPABILITY_ROLES: Record<SynapticCapability, SynapticRole[]> = {
  canViewEmail: ["administrator", "super_admin", "tenant_admin"],
  canModerate: ["administrator", "super_admin", "tenant_admin", "moderator"],
  canEdit: ["administrator", "super_admin", "tenant_admin", "editor"],
  canDevelop: ["administrator", "super_admin", "tenant_admin", "developer"],
  canAdminister: ["administrator", "super_admin", "tenant_admin"],
};

export function decodeSynapticJwt(token: string): SynapticJwtClaims | null {
  const parts = token.split(".");
  if (parts.length !== 3 || !parts[1]) return null;

  try {
    const json = decodeBase64Url(parts[1]);
    const result = synapticJwtClaimsSchema.safeParse(JSON.parse(json) as unknown);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

export function isSynapticJwtValid(token: string, nowSeconds = Math.floor(Date.now() / 1000)) {
  const claims = decodeSynapticJwt(token);
  if (!claims) return false;
  if (typeof claims.exp === "number" && claims.exp <= nowSeconds) return false;
  return true;
}

export function synapticUserFromJwt(token: string, nowSeconds = Math.floor(Date.now() / 1000)): SynapticUser | null {
  const claims = decodeSynapticJwt(token);
  if (!claims) return null;
  if (typeof claims.exp === "number" && claims.exp <= nowSeconds) return null;

  const id = claims.id ?? claims.user_id ?? claims.sub;
  if (!id) return null;

  return {
    id,
    email: claims.email,
    name: claims.name,
    picture: claims.picture || claims.avatar_url,
    roles: claims.roles.flatMap((role) => {
      const result = synapticRoleSchema.safeParse(role);
      return result.success ? [result.data] : [];
    }),
  };
}

export function hasSynapticRole(user: SynapticUser | null, role: SynapticRole) {
  return Boolean(user?.roles.includes(role));
}

export function hasAnySynapticRole(user: SynapticUser | null, roles: SynapticRole[]) {
  return Boolean(user?.roles.some((role) => roles.includes(role)));
}

export function hasSynapticCapability(user: SynapticUser | null, capability: SynapticCapability) {
  return hasAnySynapticRole(user, CAPABILITY_ROLES[capability]);
}

export function getUserInitials(user: Pick<SynapticUser, "email" | "name"> | null) {
  if (!user) return "AN";

  const source = user.name?.trim() || user.email.split("@")[0] || "AN";
  const parts = source.split(/\s+/).filter(Boolean);
  const first = parts[0];
  const second = parts[1];
  if (first && second) return `${first[0] ?? ""}${second[0] ?? ""}`.toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

export function buildSynapticIdentityUrl(
  action: "login" | "register",
  currentUrl: string,
  identityOrigin = SYNAPTIC_IDENTITY_ORIGIN,
) {
  const current = new URL(currentUrl);
  const next = `${current.pathname}${current.search}${current.hash}`;
  const callback = new URL("/auth/callback", current.origin);
  callback.searchParams.set("next", next || "/");
  callback.searchParams.set("source", action);

  const destination = new URL(`/${action}`, identityOrigin);
  destination.searchParams.set("returnUrl", callback.toString());
  return destination.toString();
}

export function extractJwtFromCallbackUrl(callbackUrl: string) {
  const url = new URL(callbackUrl);
  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));
  return hashParams.get("token") || url.searchParams.get("token");
}

export function getCallbackNextPath(callbackUrl: string) {
  const url = new URL(callbackUrl);
  const next = url.searchParams.get("next");
  if (!next || !next.startsWith("/")) return "/";
  if (next.startsWith("//")) return "/";
  return next;
}

export function getCallbackSource(callbackUrl: string): "login" | "register" | null {
  const source = new URL(callbackUrl).searchParams.get("source");
  return source === "login" || source === "register" ? source : null;
}

export function completeSynapticAuthCallback(
  callbackUrl: string,
  storage: SynapticTokenStorage,
  nowSeconds = Math.floor(Date.now() / 1000),
) {
  const source = getCallbackSource(callbackUrl);
  const token = extractJwtFromCallbackUrl(callbackUrl);
  const next = getCallbackNextPath(callbackUrl);
  let status: SynapticAuthCallbackStatus = "missing-token";

  if (source === "register") {
    storage.removeItem(JWT_STORAGE_KEY);
    status = "registration";
  } else if (token && isSynapticJwtValid(token, nowSeconds)) {
    storage.setItem(JWT_STORAGE_KEY, token);
    status = "stored";
  } else if (token) {
    storage.removeItem(JWT_STORAGE_KEY);
    status = "invalid-token";
  }

  return { next, status };
}

function decodeBase64Url(value: string) {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");

  if (typeof atob === "function") {
    return decodeURIComponent(
      Array.from(atob(padded))
        .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`)
        .join(""),
    );
  }

  return Buffer.from(padded, "base64").toString("utf8");
}

function requiredClientUrl(name: string, value: string | undefined) {
  if (!value) {
    throw new Error(`${name} is not configured`);
  }

  try {
    return new URL(value).toString().replace(/\/$/, "");
  } catch {
    throw new Error(`${name} must be a valid URL`);
  }
}
